<script setup lang="ts">
import { ref } from 'vue';
import { TrashIcon } from '@heroicons/vue/24/outline';
import { useToast } from 'vue-toastification';
import { CheckCircleIcon } from '@heroicons/vue/20/solid';

import useModal from '@/modules/shared/composable/useModal';
import ButtonsModal from '@/modules/shared/components/ButtonsModal.vue';
import useMailboxes from '@/modules/settings/composables/useMailboxes';
import useUsers from '@/modules/settings/composables/useUsers';
import useTeams from '@/modules/settings/composables/useTeams';
import useAlerts from '@/modules/explore/composables/useAlerts';
import ToastDemoComponent from '@/modules/shared/components/ToastDemoComponent.vue';

const { deleteMailboxes, getMailboxes } = useMailboxes();
const { deleteUsers, getUsers } = useUsers();
const { deleteTeams, getTeams } = useTeams();
const { deleteAlerts, getAlerts } = useAlerts();

const { deleteData, hideModal } = useModal();
const toast = useToast();

const isDisabled = ref<boolean>(false);

const deleteFromBq = ref(false);

const remove = () => {
    isDisabled.value = true;
    if (deleteData.value.type === 'mailboxes') {
        deleteMailboxes({ data: { emails: deleteData.value.data, delete_from_bq: deleteFromBq.value } }).then(async () => {
            await getMailboxes();
            hideModal();
            toast.success(ToastDemoComponent, { icon: CheckCircleIcon });
        });
    }
    if (deleteData.value.type === 'users') {
        deleteUsers({ data: { emails: deleteData.value.data } }).then(async () => {
            await getUsers();
            hideModal();
            toast.success(ToastDemoComponent, { icon: CheckCircleIcon });
        });
    }
    if (deleteData.value.type === 'teams') {
        deleteTeams({ data: { teams: deleteData.value.data } }).then(async () => {
            await getTeams();
            getUsers();
            hideModal();
            toast.success(ToastDemoComponent, { icon: CheckCircleIcon });
        });
    }
    if (deleteData.value.type === 'alerts') {
        deleteAlerts({ data: { alerts: deleteData.value.data } }).then(async () => {
            await getAlerts();
            hideModal();
            toast.success(ToastDemoComponent, { icon: CheckCircleIcon });
        });
    }
    isDisabled.value = true;
};
</script>

<template>
    <div class="p-6 pb-0 overflow-auto">
        <div class="bg-red-100 w-12 h-12 flex items-center justify-center rounded-full">
            <TrashIcon class="w-6 h-6 text-red-500" />
        </div>

        <p class="text-gray-900 font-semibold leading-8 text-lg mt-5">{{ deleteData.title }}</p>
        <p class="text-sm leading-5 font-normal text-gray-500 pt-2">{{ deleteData.text }}</p>

        <div v-if="deleteData.type === 'mailboxes'" class="pt-5">
            <fieldset>
                <div class="relative flex items-start">
                    <div class="flex h-6 items-center">
                        <input
                            id="remove"
                            v-model="deleteFromBq"
                            aria-describedby="remove-description"
                            name="remove"
                            type="checkbox"
                            class="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-0 focus:ring-offset-0"
                        />
                        <p class="ml-2 text-sm leading-5 font-medium text-gray-700">Delete all their existing email data from the dashboard</p>
                    </div>
                </div>
            </fieldset>
        </div>
    </div>

    <ButtonsModal type="Remove" color="red" :isDisabled="isDisabled" @submit="remove" />
</template>

<style scoped></style>
